import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { Copy, Mail, MessageCircle, MessageSquare, Share2 } from "lucide-react";
import { toast } from "sonner";

// TODO(content): swap with a real featured student page that consented to be highlighted
const FEATURED = {
    name: "Maria",
    field: "Environmental Science · UC Berkeley",
    monthlyDonated: 1590,
    supporters: 41,
    // Hardcoded (not window.location.origin): the puppeteer prerender pass
    // navigates to a local preview server, so window.location.origin at
    // build time is that local address (e.g. http://127.0.0.1:8000), which
    // was getting baked into the static homepage HTML's share links.
    pageUrl: "https://mystudyfund.com/fund/demo",
    shareMessage:
        "I just found Maria's Fund Page on My Study Fund — she's a first-gen student putting herself through Berkeley. Even $20 helps. Take a look:",
};

const ShareSection = () => {
    const { ref, isVisible } = useScrollAnimation(0.05);

    const url = FEATURED.pageUrl;
    const message = FEATURED.shareMessage;
    const encodedMessage = encodeURIComponent(message);
    const encodedUrl = encodeURIComponent(url);
    const fullMsgAndUrl = encodeURIComponent(`${message} ${url}`);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(`${message} ${url}`);
            toast.success("Link copied — paste it in a group chat");
        } catch {
            toast.error("Couldn't copy. Try again?");
        }
    };

    const handleNativeShare = async () => {
        try {
            if (typeof navigator !== "undefined" && (navigator as Navigator & { share?: (data: ShareData) => Promise<void> }).share) {
                await (navigator as Navigator & { share: (data: ShareData) => Promise<void> }).share({
                    title: `Back ${FEATURED.name} on My Study Fund`,
                    text: message,
                    url,
                });
            } else {
                await handleCopy();
            }
        } catch {
            /* user cancelled */
        }
    };


    return (
        <section
            ref={ref}
            id="share"
            className="py-20 md:py-28 relative overflow-hidden"
            style={{
                background:
                    "radial-gradient(900px 500px at 0% 0%, hsl(var(--gold) / 0.18), transparent 60%), radial-gradient(900px 500px at 100% 100%, hsl(var(--secondary) / 0.25), transparent 60%), #FFFFFF",
            }}
        >
            <div className="max-w-6xl mx-auto px-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
                    <div
                        className={`transition-all duration-700 ${
                            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                        }`}
                    >
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/5 border border-primary/10 mb-4">
                            <Share2 className="w-3.5 h-3.5 text-primary" />
                            <span className="text-xs font-bold tracking-wide text-primary uppercase">
                                One tap can change a semester
                            </span>
                        </div>
                        <h2 className="text-3xl md:text-5xl font-extrabold text-primary leading-tight tracking-tight">
                            Don't just sign up.{" "}
                            <span
                                className="bg-clip-text text-transparent"
                                style={{
                                    backgroundImage:
                                        "linear-gradient(90deg, hsl(var(--gold)), hsl(var(--primary)))",
                                }}
                            >
                                Send this to someone.
                            </span>
                        </h2>
                        <p className="text-lg text-slate-600 mt-4 max-w-xl leading-relaxed">
                            Most contributions come through a parent, an aunt, or a coach — not a stranger.
                            If a student in your life could use this, share their page (or this one) right now.
                        </p>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-7">
                            <button
                                onClick={handleNativeShare}
                                className="flex flex-col items-center justify-center gap-2 p-4 rounded-2xl bg-primary text-white hover:bg-primary/90 transition-all hover:-translate-y-0.5 shadow-md shadow-primary/10"
                                aria-label="Share via your device"
                            >
                                <Share2 className="w-5 h-5" />
                                <span className="text-xs font-semibold">Share</span>
                            </button>

                            <a
                                href={`https://wa.me/?text=${fullMsgAndUrl}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex flex-col items-center justify-center gap-2 p-4 rounded-2xl bg-white border border-slate-200 text-slate-800 hover:border-emerald-400 hover:text-emerald-600 hover:-translate-y-0.5 transition-all shadow-sm"
                                aria-label="Share on WhatsApp"
                            >
                                <MessageCircle className="w-5 h-5" />
                                <span className="text-xs font-semibold">WhatsApp</span>
                            </a>

                            <a
                                href={`sms:?&body=${fullMsgAndUrl}`}
                                className="flex flex-col items-center justify-center gap-2 p-4 rounded-2xl bg-white border border-slate-200 text-slate-800 hover:border-primary/40 hover:text-primary hover:-translate-y-0.5 transition-all shadow-sm"
                                aria-label="Share via SMS"
                            >
                                <MessageSquare className="w-5 h-5" />
                                <span className="text-xs font-semibold">Text</span>
                            </a>

                            <a
                                href={`mailto:?subject=${encodeURIComponent(
                                    `Back ${FEATURED.name} on My Study Fund`
                                )}&body=${encodedMessage}%0A%0A${encodedUrl}`}
                                className="flex flex-col items-center justify-center gap-2 p-4 rounded-2xl bg-white border border-slate-200 text-slate-800 hover:border-primary/40 hover:text-primary hover:-translate-y-0.5 transition-all shadow-sm"
                                aria-label="Share via email"
                            >
                                <Mail className="w-5 h-5" />
                                <span className="text-xs font-semibold">Email</span>
                            </a>
                        </div>

                        <button
                            onClick={handleCopy}
                            className="mt-3 w-full inline-flex items-center justify-center gap-2 p-3.5 rounded-2xl bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors text-sm font-semibold"
                        >
                            <Copy className="w-4 h-4" />
                            Copy link &amp; message
                        </button>

                        <p className="text-xs text-slate-500 mt-4">
                            Sharing a Fund Page is the #1 thing that turns a stuck student into a funded one.
                        </p>
                    </div>

                    {/* Featured Fund Page card preview */}
                    <div
                        className={`relative transition-all duration-700 delay-100 ${
                            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                        }`}
                    >
                        <div className="absolute -inset-4 rounded-[2rem] opacity-30 blur-2xl"
                             style={{ background: "linear-gradient(135deg, hsl(var(--gold)), hsl(var(--secondary)))" }} />
                        <div className="relative bg-white rounded-3xl overflow-hidden ring-1 ring-black/5 shadow-2xl shadow-primary/10">
                            <div className="relative aspect-[5/4]">
                                <img
                                    src="/student-smiling.jpg"
                                    alt={`${FEATURED.name}'s Fund Page`}
                                    className="absolute inset-0 w-full h-full object-cover"
                                />
                                <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur text-xs font-bold text-primary">
                                    Featured Fund Page
                                </div>
                            </div>
                            <div className="p-6">
                                <div className="flex items-center justify-between gap-2 mb-1">
                                    <h3 className="text-xl font-bold text-primary">
                                        {FEATURED.name}
                                    </h3>
                                    <span className="text-xs text-slate-500">
                                        {FEATURED.supporters} supporters
                                    </span>
                                </div>
                                <p className="text-sm text-slate-600 mb-4">{FEATURED.field}</p>

                                <div className="flex items-baseline gap-2 mt-1">
                                    <span className="text-2xl font-extrabold text-primary leading-none">
                                        ${FEATURED.monthlyDonated.toLocaleString()}
                                    </span>
                                    <span className="text-sm text-slate-500">
                                        donated last month
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ShareSection;
